import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Completes sign-in. The Worker redirects here with the session token in the
 * URL fragment (`#session_token=...`) — fragments are never sent to a server,
 * so the token stays out of access logs and Referer headers. It is read once,
 * persisted, then stripped from the address bar.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const match = (window.location.hash || "").match(/session_token=([^&]+)/);
    const token = match ? decodeURIComponent(match[1]) : null;

    const run = async () => {
      if (!token) {
        navigate("/", { replace: true });
        return;
      }
      signIn(token, null);
      // Remove the token from the URL before any further navigation so it is
      // never captured in browser history.
      window.history.replaceState({}, document.title, "/dashboard");
      try {
        const res = await api.get("/auth/me");
        signIn(token, res.data);
        navigate("/dashboard", { replace: true });
      } catch {
        navigate("/?auth_error=session_failed", { replace: true });
      }
    };
    run();
  }, [navigate, signIn]);

  return (
    <div className="min-h-dvh flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="w-10 h-10 border-2 border-income border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-muted-foreground text-sm tracking-wide">Signing you in…</p>
      </div>
    </div>
  );
}
